/**
 * Unit conversion for simulation result columns, keyed by the parquet column
 * metadata (resultAttributes: { unit, unitType }). Shared by the Results chart
 * and the Compare modal so both convert a column identically.
 *
 * Every factor comes from shared/utils/unitConversions.js.
 */

import {
  GRAMS_PER_METAL_UNIT,
  MASS_FROM_KG,
  gradeFactor,
  gradeFactorFromCombined,
  M3_PER_VOLUME_UNIT,
  M2_PER_AREA_UNIT,
  M_PER_LENGTH_UNIT,
  KPA_PER_PRESSURE_UNIT,
  MM_PER_PRECIP_UNIT,
  MS_PER_WIND_UNIT,
  DAYS_PER_TIME_UNIT,
} from "../../../shared/utils/unitConversions";

/** Grams in one unit of ORE mass ("oz" = avoirdupois), or undefined if unknown. */
const gramsPerOreUnit = (u) => (MASS_FROM_KG[u] ? 1000 / MASS_FROM_KG[u] : undefined);

/**
 * Which Chart.js y-axis should a column of this unitType be plotted on.
 * Returns "yMass" | "yPct" | "yVol".
 */
export function axisForUnitType(unitType) {
  if (unitType === "percentage_ratio" || unitType === "rate_per_time") return "yPct";
  if (unitType === "volume" || unitType === "flow_rate" || unitType === "precipitation") return "yVol";
  return "yMass";
}

/**
 * Convert an array of raw values from the parquet base unit to the user's
 * preferred display unit. Returns { converted, displayUnit }.
 */
export function convertArrayByUnitType(arr, baseUnit, unitType, unitPreferences) {
  if (!arr || !unitType) return { converted: arr, displayUnit: baseUnit ?? "" };

  const norm = (u) => (u ?? "").toLowerCase().trim();
  const mapArr = (k) => (k === 1 ? arr : arr.map(v => (v == null ? null : v * k)));

  switch (unitType) {

    case "metal_mass": {
      const target  = unitPreferences?.metalRecovery?.massRecovery ?? "g";
      const baseG   = GRAMS_PER_METAL_UNIT[baseUnit] ?? GRAMS_PER_METAL_UNIT[norm(baseUnit)] ?? 1;
      const tgtG    = GRAMS_PER_METAL_UNIT[target] ?? 1;
      return { converted: mapArr(baseG / tgtG), displayUnit: target };
    }

    case "mass": {
      const target  = unitPreferences?.physical?.mass ?? "kg";
      const baseG   = gramsPerOreUnit(baseUnit) ?? gramsPerOreUnit(norm(baseUnit)) ?? 1;
      const tgtG    = gramsPerOreUnit(target) ?? 1;
      return { converted: mapArr(baseG / tgtG), displayUnit: target };
    }

    case "volume": {
      const target  = unitPreferences?.physical?.volume ?? "m³";
      const baseM3  = M3_PER_VOLUME_UNIT[baseUnit] ?? M3_PER_VOLUME_UNIT[norm(baseUnit)] ?? 1;
      const tgtM3   = M3_PER_VOLUME_UNIT[target] ?? 1;
      return { converted: mapArr(baseM3 / tgtM3), displayUnit: target };
    }

    case "flow_rate": {
      // Stored as volume/time, e.g. "m3/day".
      const { volume: vUnit = "L", time: tUnit = "min" } = unitPreferences?.leaching?.flowRate ?? {};
      const [bVol = "m3", bTime = "day"] = (baseUnit ?? "m3/day").split("/");
      const baseM3   = M3_PER_VOLUME_UNIT[bVol] ?? 1;
      const baseDays = DAYS_PER_TIME_UNIT[norm(bTime)] ?? 1;
      const tgtM3    = M3_PER_VOLUME_UNIT[vUnit] ?? 0.001;
      const tgtDays  = DAYS_PER_TIME_UNIT[norm(tUnit)] ?? 1 / 1440;
      // value[bVol/bTime] → value[vUnit/tUnit]:
      // numerator: baseM3 → tgtM3  (÷ baseM3/tgtM3)
      // denominator: baseDays/tUnit → keep ratio of bTime per tUnit
      const k = (baseM3 / tgtM3) * (tgtDays / baseDays);
      const combined = unitPreferences?.leaching?.flowRate?.combined ?? `${vUnit}/${tUnit}`;
      return { converted: mapArr(k), displayUnit: combined };
    }

    case "application_rate": {
      // Stored as depth/time, e.g. "m/s" (equivalent to m³/m²/s).
      const { volume: vUnit = "L", time: tUnit = "h", area: aUnit = "m²" } = unitPreferences?.leaching?.applicationRate ?? {};
      // Convert base (m/s = 1 m³/m²/s) → vUnit/tUnit/aUnit:
      //   1 m/s = 1 m depth/s over 1 m² = (1/tgtM3) tgtVol/s/tgtArea
      //                                  = (1/tgtM3) * tgtSec/tgtM2 per tUnit per tgtArea
      const tgtM3  = M3_PER_VOLUME_UNIT[vUnit] ?? 0.001;
      const tgtM2  = M2_PER_AREA_UNIT[aUnit] ?? 1;
      const tgtSec = (DAYS_PER_TIME_UNIT[norm(tUnit)] ?? 1 / 24) * 86400; // seconds per tUnit
      const baseSec = baseUnit === "m/day" ? 1 / 86400 : 1; // m/day → m/s scaling
      const k = (1 / tgtM3) * tgtSec * baseSec / tgtM2;
      const combined = unitPreferences?.leaching?.applicationRate?.combined ?? `${vUnit}/${tUnit}/${aUnit}`;
      return { converted: mapArr(k), displayUnit: combined };
    }

    case "concentration": {
      const { mass: mUnit = "g", volume: vUnit = "L" } = unitPreferences?.metalRecovery?.concentration ?? {};
      // Parse base: "g/m3" → massPart="g", volPart="m3"
      const [bMass = "g", bVol = "m3"] = (baseUnit ?? "g/m3").split("/");
      const baseG   = GRAMS_PER_METAL_UNIT[bMass] ?? 1;
      const baseM3  = M3_PER_VOLUME_UNIT[bVol] ?? M3_PER_VOLUME_UNIT["m3"] ?? 1;
      const tgtG    = GRAMS_PER_METAL_UNIT[mUnit] ?? 1;
      const tgtM3   = M3_PER_VOLUME_UNIT[vUnit] ?? 0.001;
      // k = (baseG/tgtG) * (tgtM3/baseM3)
      const k = (baseG / tgtG) * (tgtM3 / baseM3);
      return { converted: mapArr(k), displayUnit: `${mUnit}/${vUnit}` };
    }

    case "ore_grade": {
      const { metalMass: mUnit = "g", oreMass: oUnit = "t", combined } = unitPreferences?.metalRecovery?.gradeOre ?? {};
      // Base (parquet) grade → g/kg → the preferred grade unit, both resolved by
      // the central grade resolver (metal "oz" = troy, "t" = metric, "ton" = short).
      const baseF = gradeFactor(baseUnit || "g/t") ?? gradeFactor(norm(baseUnit)) ?? 1000;
      const { factor: tgtF, unit } = gradeFactorFromCombined(combined ?? `${mUnit}/${oUnit}`);
      return { converted: mapArr(tgtF / baseF), displayUnit: unit };
    }

    case "area": {
      const target = unitPreferences?.physical?.area ?? "m²";
      const baseM2 = M2_PER_AREA_UNIT[baseUnit] ?? 1;
      const tgtM2  = M2_PER_AREA_UNIT[target] ?? 1;
      return { converted: mapArr(baseM2 / tgtM2), displayUnit: target };
    }

    case "length": {
      const target = unitPreferences?.physical?.length ?? "m";
      const baseM  = M_PER_LENGTH_UNIT[baseUnit] ?? 1;
      const tgtM   = M_PER_LENGTH_UNIT[target] ?? 1;
      return { converted: mapArr(baseM / tgtM), displayUnit: target };
    }

    case "height": {
      const target = unitPreferences?.physical?.heapHeight ?? "m";
      const baseM  = M_PER_LENGTH_UNIT[baseUnit] ?? 1;
      const tgtM   = M_PER_LENGTH_UNIT[target] ?? 1;
      return { converted: mapArr(baseM / tgtM), displayUnit: target };
    }

    case "temperature": {
      const target = unitPreferences?.climate?.temperature ?? unitPreferences?.physical?.temperature ?? "°C";
      if (target === baseUnit) return { converted: arr, displayUnit: target };
      const convertTemp = (v) => {
        if (v == null) return null;
        // Normalise to Celsius first
        let c = v;
        if (baseUnit === "°F" || baseUnit === "F") c = (v - 32) * 5 / 9;
        if (baseUnit === "K") c = v - 273.15;
        // Celsius → target
        if (target === "°F" || target === "F") return c * 9 / 5 + 32;
        if (target === "K") return c + 273.15;
        return c;
      };
      return { converted: arr.map(convertTemp), displayUnit: target };
    }

    case "pressure": {
      const target   = unitPreferences?.physical?.pressure ?? unitPreferences?.climate?.pressure ?? "kPa";
      const baseKPa  = KPA_PER_PRESSURE_UNIT[baseUnit] ?? 1;
      const tgtKPa   = KPA_PER_PRESSURE_UNIT[target] ?? 1;
      return { converted: mapArr(baseKPa / tgtKPa), displayUnit: target };
    }

    case "density": {
      const { mass: mUnit = "kg", volume: vUnit = "m³", combined } = unitPreferences?.physical?.density ?? {};
      const [bMass = "kg", bVol = "m3"] = (baseUnit ?? "kg/m3").split("/");
      const baseG   = gramsPerOreUnit(bMass) ?? 1000;
      const baseM3  = M3_PER_VOLUME_UNIT[bVol] ?? 1;
      const tgtG    = gramsPerOreUnit(mUnit) ?? 1000;
      const tgtM3   = M3_PER_VOLUME_UNIT[vUnit] ?? 1;
      const k = (baseG / tgtG) * (tgtM3 / baseM3);
      return { converted: mapArr(k), displayUnit: combined ?? `${mUnit}/${vUnit}` };
    }

    case "precipitation": {
      const target  = unitPreferences?.climate?.precipitation ?? "mm";
      const baseMM  = MM_PER_PRECIP_UNIT[baseUnit] ?? 1;
      const tgtMM   = MM_PER_PRECIP_UNIT[target] ?? 1;
      return { converted: mapArr(baseMM / tgtMM), displayUnit: target };
    }

    case "wind_speed": {
      const target  = unitPreferences?.climate?.windSpeed ?? "km/h";
      const baseMS  = MS_PER_WIND_UNIT[baseUnit] ?? MS_PER_WIND_UNIT[norm(baseUnit)] ?? 1 / 3.6;
      const tgtMS   = MS_PER_WIND_UNIT[target] ?? 1 / 3.6;
      return { converted: mapArr(baseMS / tgtMS), displayUnit: target };
    }

    case "time": {
      const target    = unitPreferences?.timeRates?.time ?? "hours";
      const baseDays  = DAYS_PER_TIME_UNIT[norm(baseUnit)] ?? 1;
      const tgtDays   = DAYS_PER_TIME_UNIT[norm(target)] ?? 1 / 24;
      return { converted: mapArr(baseDays / tgtDays), displayUnit: target };
    }

    case "rate_per_time": {
      // e.g. "%/day" or "fraction/day"
      const { rate: rUnit = "%", time: tUnit = "day", combined } = unitPreferences?.timeRates?.recoveryRate ?? {};
      const [bRate = "%", bTime = "day"] = (baseUnit ?? "%/day").split("/");
      const rateFactor = (bRate === "fraction" || bRate === "1") ? 100 : 1;
      const bDays = DAYS_PER_TIME_UNIT[norm(bTime)] ?? 1;
      const tDays = DAYS_PER_TIME_UNIT[norm(tUnit)] ?? 1;
      const k = rateFactor * (bDays / tDays);
      return { converted: mapArr(k), displayUnit: combined ?? `${rUnit}/${tUnit}` };
    }

    case "percentage_ratio": {
      const target = unitPreferences?.timeRates?.ratioDisplay ?? "%";
      // fraction (0–1) → %
      const isFraction = baseUnit === "fraction" || baseUnit === "dimensionless";
      return {
        converted: isFraction ? arr.map(v => (v == null ? null : v * 100)) : arr,
        displayUnit: target,
      };
    }

    case "unitless":
    default:
      return { converted: arr, displayUnit: baseUnit ?? "" };
  }
}
